import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DashboardHeader from '../components/DashboardHeader';
import TermsModal from '../components/TermsModal';
import './Home.css';
import '../styles/pageAnimation.css';

function AboutUs() {
  const [isMobile, setIsMobile] = useState(false);
  const [showGreenBar, setShowGreenBar] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Scroll to top on mount - aggressive approach
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hide green bar on scroll (no animation, instant show/hide)
  useEffect(() => {
    const handleScroll = () => {
      setShowGreenBar(window.scrollY === 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) - only visible at top */}
      {showGreenBar && (
        <div style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
          {/* Empty green bar for accent */}
        </div>
      )}
      {/* Navbar always sticky at top */}
      <div style={{ 
        width: '100%', 
        background: 'rgba(255, 255, 255, 0.98)', 
        backdropFilter: 'blur(10px)', 
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)', 
        position: 'sticky',
        top: 0, 
        zIndex: 9998,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </div>
      <div style={{ width: '100%', flex: 1 }} className="page-content-fade-in">
      
      {/* Main Content */}
      <div style={{ 
        width: '100%', 
        maxWidth: isMobile ? '100%' : '1200px', 
        margin: isMobile ? '0' : '60px auto 40px auto', 
        padding: isMobile ? '0' : '0 20px'
      }}>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{ 
            background: 'white', 
            borderRadius: isMobile ? '0' : '24px', 
            padding: isMobile ? '24px 20px' : '56px 48px', 
            boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0,0,0,0.08)', 
            position: 'relative',
            margin: isMobile ? '0' : '0 auto'
          }}>
          
          {/* Hero Section with Logo */}
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ 
              textAlign: 'center',
              marginBottom: isMobile ? '32px' : '48px',
              position: 'relative'
            }}>
            
            {/* Logo Container */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4, type: "spring", stiffness: 100 }}
              style={{ 
                display: 'inline-block',
                background: 'white',
                borderRadius: '50%',
                padding: isMobile ? '16px' : '20px',
                marginBottom: '24px',
                boxShadow: '0 8px 32px rgba(23, 92, 54, 0.25), 0 2px 8px rgba(0,0,0,0.1)',
                border: '3px solid #175c36',
                position: 'relative'
              }}>
              {/* Glow effect */}
              <div style={{
                position: 'absolute',
                inset: '-10px',
                background: 'radial-gradient(circle, rgba(23, 92, 54, 0.3) 0%, transparent 70%)',
                borderRadius: '50%',
                zIndex: -1,
                animation: 'pulse 3s ease-in-out infinite'
              }}/>
              <img 
                src="/images/chiqui_logo.svg" 
                alt="Chiqui's Garden Logo" 
                style={{ 
                  width: isMobile ? '64px' : '80px', 
                  height: isMobile ? '64px' : '80px', 
                  display: 'block'
                }}
              />
            </motion.div>
            
            <motion.h1 
              {...fadeInUp}
              transition={{ delay: 0.5 }}
              style={{ 
                fontSize: isMobile ? '2rem' : '3rem', 
                fontWeight: 800, 
                background: 'linear-gradient(135deg, #175c36 0%, #2d8659 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '16px',
                lineHeight: 1.2,
                letterSpacing: '-0.5px'
              }}>
              About Chiqui's Garden
            </motion.h1>
            
            <motion.p
              {...fadeInUp}
              transition={{ delay: 0.6 }}
              style={{
                fontSize: isMobile ? '1rem' : '1.15rem',
                color: '#666',
                maxWidth: '700px',
                margin: '0 auto',
                lineHeight: 1.6,
                fontWeight: 400
              }}>
              Growing green dreams, one plant at a time 🌱
            </motion.p>
          </motion.div>

          {/* Content Sections */}
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '32px' : '48px'
            }}>
            
            {/* Main Story */}
            <motion.section variants={fadeInUp}>
              <div style={{
                background: 'linear-gradient(135deg, #f8fdf9 0%, #e8f5ea 100%)',
                borderRadius: '20px',
                padding: isMobile ? '24px' : '32px',
                border: '1px solid rgba(23, 92, 54, 0.1)',
                boxShadow: '0 4px 16px rgba(23, 92, 54, 0.08)'
              }}>
                <p style={{ 
                  fontSize: isMobile ? '1rem' : '1.1rem', 
                  lineHeight: 1.8, 
                  color: '#333', 
                  marginBottom: '20px',
                  textAlign: 'justify'
                }}>
                  At <strong style={{ color: '#175c36', fontWeight: 600 }}>Chiqui's Garden</strong>, we believe that every space—big or small—deserves a touch of nature. 
                  Founded with a passion for greenery and design, we provide a carefully curated selection of plants, pots, 
                  landscaping materials, and gardening essentials tailored for every kind of plant lover.
                </p>
                
                <p style={{ 
                  fontSize: isMobile ? '1rem' : '1.1rem', 
                  lineHeight: 1.8, 
                  color: '#333',
                  textAlign: 'justify'
                }}>
                  Whether you're a beginner starting your first garden or a seasoned landscaper, our goal is to make gardening more accessible, 
                  enjoyable, and sustainable. We're your partner in transforming homes and outdoor spaces into refreshing green sanctuaries.
                </p>
              </div>
            </motion.section>

            {/* Mission Card */}
            <motion.section variants={fadeInUp}>
              <div style={{
                background: 'white',
                borderRadius: '20px',
                padding: isMobile ? '24px' : '32px',
                border: '2px solid #175c36',
                boxShadow: '0 8px 24px rgba(23, 92, 54, 0.12)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Decorative corner */}
                <div style={{
                  position: 'absolute',
                  top: '-1px',
                  right: '-1px',
                  width: '120px',
                  height: '120px',
                  background: 'linear-gradient(225deg, rgba(23, 92, 54, 0.08) 0%, rgba(23, 92, 54, 0.08) 50%, transparent 50%)',
                  pointerEvents: 'none',
                  borderTopRightRadius: isMobile ? '18px' : '18px'
                }}/>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', position: 'relative', zIndex: 1 }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #175c36 0%, #1a6b3f 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(23, 92, 54, 0.3)'
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                      <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V7.3l7-3.11v8.8z"/>
                    </svg>
                  </div>
                  <h2 style={{ 
                    fontSize: isMobile ? '1.5rem' : '2rem', 
                    fontWeight: 700, 
                    color: '#175c36',
                    margin: 0
                  }}>
                    Our Mission
                  </h2>
                </div>
                
                <p style={{ 
                  fontSize: isMobile ? '1rem' : '1.1rem', 
                  lineHeight: 1.8, 
                  color: '#444',
                  marginBottom: 0,
                  textAlign: 'justify'
                }}>
                  Our mission is to inspire and empower plant lovers of all levels—from beginners to experienced gardeners—to cultivate their own 
                  green spaces. We believe that plants not only beautify our surroundings but also improve our well-being and connection to nature.
                </p>
              </div>
            </motion.section>

            {/* What We Offer - Grid Layout */}
            <motion.section variants={fadeInUp}>
              <h2 style={{ 
                fontSize: isMobile ? '1.75rem' : '2.25rem', 
                fontWeight: 700, 
                color: '#175c36',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                What We Offer
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: isMobile ? '16px' : '20px'
              }}>
                {[
                  { 
                    icon: (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M12 3L4 9v12h16V9l-8-6z" fill="#8B4513"/>
                        <rect x="10" y="14" width="4" height="7" fill="#654321"/>
                        <path d="M2 9h20M12 3v6M6 9v12M18 9v12" stroke="#175c36" strokeWidth="1.5"/>
                        <circle cx="8" cy="13" r="1.5" fill="#FF6B6B"/>
                        <circle cx="16" cy="13" r="1.5" fill="#4ECDC4"/>
                        <path d="M3 21h18" stroke="#175c36" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    ), 
                    title: 'Landscaping Services', 
                    desc: 'Professional landscape design and installation for your perfect outdoor space' 
                  },
                  { 
                    icon: (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M6 2h12l-2 4h-8L6 2z" fill="#424242"/>
                        <path d="M8 6c-1 2-1 4 0 6 .5 1 1 2 .5 3-.5 1-1 2-1 3h9c0-1-.5-2-1-3-.5-1 0-2 .5-3 1-2 1-4 0-6H8z" fill="#2196F3"/>
                        <path d="M9 9c0 1.5.3 2.5.7 3.5.4 1 .3 1.5.1 2.5h4.4c-.2-1-.3-1.5.1-2.5.4-1 .7-2 .7-3.5H9z" fill="#42A5F5"/>
                        <path d="M10 12c0 1 .2 1.8.4 2.5h3.2c.2-.7.4-1.5.4-2.5h-4z" fill="#64B5F6"/>
                        <ellipse cx="12" cy="18.5" rx="6" ry="1.5" fill="#1565C0"/>
                        <path d="M8 18c0 .8 1.8 1.5 4 1.5s4-.7 4-1.5" fill="#1976D2"/>
                        <path d="M10 7l1 2M14 7l-1 2M12 9v2" stroke="#E3F2FD" strokeWidth="1" opacity="0.6"/>
                      </svg>
                    ), 
                    title: 'Waterfalls', 
                    desc: 'Beautiful custom waterfall features to enhance your garden' 
                  },
                  { 
                    icon: (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M3 20h18v2H3v-2z" fill="#5D4037"/>
                        <path d="M5 20c0-3 2-5 3-7 1-2 1-4 2-6h4c1 2 1 4 2 6 1 2 3 4 3 7H5z" fill="#616161"/>
                        <path d="M6 20c0-2.5 1.5-4 2.5-6 .8-1.5 1-3 1.5-5h4c.5 2 .7 3.5 1.5 5 1 2 2.5 3.5 2.5 6H6z" fill="#757575"/>
                        <path d="M8 20c0-2 1-3 1.5-4.5.5-1.5.7-3 1-4.5h3c.3 1.5.5 3 1 4.5.5 1.5 1.5 2.5 1.5 4.5H8z" fill="#9E9E9E"/>
                        <rect x="10" y="14" width="4" height="6" rx="2" fill="#424242"/>
                        <path d="M10 15c0-.5.5-1 1-1h2c.5 0 1 .5 1 1v5h-4v-5z" fill="#212121"/>
                        <path d="M7 12l1-2 1 2-1 1-1-1zM15 12l1-2 1 2-1 1-1-1z" fill="#424242"/>
                      </svg>
                    ), 
                    title: 'Grotto', 
                    desc: 'Unique grotto designs that add character to your landscape' 
                  },
                  { 
                    icon: (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <rect x="2" y="16" width="20" height="6" fill="#8B4513"/>
                        <path d="M4 16c0-2 1-3 2-4 1-1 1-2 1-3" stroke="#7CB342" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M7 16c0-2 1-3 2-4 1-1 1-2 1-3" stroke="#9CCC65" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M10 16c0-2 1-3 2-4 1-1 1-2 1-3" stroke="#7CB342" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M13 16c0-2 1-3 2-4 1-1 1-2 1-3" stroke="#AED581" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M16 16c0-2 1-3 2-4 1-1 1-2 1-3" stroke="#9CCC65" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M19 16c0-2 1-3 2-4 1-1 1-2 1-3" stroke="#7CB342" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    ), 
                    title: 'Grass Planting', 
                    desc: 'Expert grass planting and lawn installation services' 
                  },
                  { 
                    icon: (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="2" width="2" height="20" fill="#8BC34A" rx="1"/>
                        <rect x="7" y="2" width="2" height="20" fill="#7CB342" rx="1"/>
                        <rect x="11" y="2" width="2" height="20" fill="#8BC34A" rx="1"/>
                        <rect x="15" y="2" width="2" height="20" fill="#9CCC65" rx="1"/>
                        <rect x="19" y="2" width="2" height="20" fill="#7CB342" rx="1"/>
                        <line x1="3" y1="6" x2="21" y2="6" stroke="#6B8E23" strokeWidth="1.5"/>
                        <line x1="3" y1="10" x2="21" y2="10" stroke="#6B8E23" strokeWidth="1.5"/>
                        <line x1="3" y1="14" x2="21" y2="14" stroke="#6B8E23" strokeWidth="1.5"/>
                        <line x1="3" y1="18" x2="21" y2="18" stroke="#6B8E23" strokeWidth="1.5"/>
                        <circle cx="4" cy="4" r="0.8" fill="#F4E04D"/>
                        <circle cx="8" cy="8" r="0.8" fill="#F4E04D"/>
                        <circle cx="12" cy="12" r="0.8" fill="#F4E04D"/>
                      </svg>
                    ), 
                    title: 'Bamboo Fencing', 
                    desc: 'Eco-friendly bamboo fencing for privacy and aesthetics' 
                  },
                  { 
                    icon: (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <ellipse cx="12" cy="19" rx="2.5" ry="0.8" fill="#8D6E63"/>
                        <rect x="5" y="17" width="4" height="2" rx="0.5" fill="#A0826D"/>
                        <ellipse cx="7" cy="17" rx="2" ry="0.6" fill="#D7CCC8"/>
                        <rect x="15" y="17" width="4" height="2" rx="0.5" fill="#A0826D"/>
                        <ellipse cx="17" cy="17" rx="2" ry="0.6" fill="#D7CCC8"/>
                        <rect x="9" y="16" width="6" height="2.5" rx="0.5" fill="#8D6E63"/>
                        <ellipse cx="12" cy="16" rx="3" ry="0.8" fill="#D7CCC8"/>
                        <rect x="7" y="15.5" width="3" height="1.5" rx="0.4" fill="#A0826D"/>
                        <rect x="14" y="15.5" width="3" height="1.5" rx="0.4" fill="#A0826D"/>
                        <path d="M12 5c-1.5 2-2 4-1.5 6 .5 1.5 2 2 1.5 3.5-.3.8-.7 1.5-1 2" fill="#FFC107" stroke="#F57C00" strokeWidth="0.5"/>
                        <path d="M11 7c-1 1.5-1.3 3-.8 4.5.5 1.2 1.5 1.5 1 2.5-.3.6-.6 1.2-.8 1.5" fill="#FFD54F"/>
                        <path d="M13 7c1 1.5 1.3 3 .8 4.5-.5 1.2-1.5 1.5-1 2.5.3.6.6 1.2.8 1.5" fill="#FFE082"/>
                        <path d="M10 9c-.5 1-.7 2-.3 3 .4.8 1 1 .5 1.8-.2.4-.4.8-.5 1.2" fill="#FFAB40" stroke="#FF6F00" strokeWidth="0.3"/>
                        <path d="M14 9c.5 1 .7 2 .3 3-.4.8-1 1-.5 1.8.2.4.4.8.5 1.2" fill="#FFAB40" stroke="#FF6F00" strokeWidth="0.3"/>
                      </svg>
                    ), 
                    title: 'Bonfire', 
                    desc: 'Custom bonfire pit design and installation' 
                  },
                  { 
                    icon: (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M9 13c0-2 .5-3.5 1-5 .3-.8.5-1.5.5-2.5 0 0 0 0 0 0" stroke="#6D4C41" strokeWidth="1.5" fill="none"/>
                        <path d="M12 13c0-2 0-4 0-6" stroke="#6D4C41" strokeWidth="1.5" fill="none"/>
                        <path d="M15 13c0-2-.5-3.5-1-5-.3-.8-.5-1.5-.5-2.5" stroke="#6D4C41" strokeWidth="1.5" fill="none"/>
                        <path d="M7 8c0-1.5 1-2.5 2-3 1-.5 2-.5 3 0" fill="#7CB342"/>
                        <path d="M8 7c0-1.2.8-2 1.5-2.5.8-.4 1.5-.4 2.2 0" fill="#8BC34A"/>
                        <path d="M17 8c0-1.5-1-2.5-2-3-1-.5-2-.5-3 0" fill="#9CCC65"/>
                        <path d="M16 7c0-1.2-.8-2-1.5-2.5-.8-.4-1.5-.4-2.2 0" fill="#AED581"/>
                        <path d="M7 11c0-1 .7-1.8 1.5-2.2.8-.4 1.7-.4 2.5 0" fill="#66BB6A"/>
                        <path d="M17 11c0-1-.7-1.8-1.5-2.2-.8-.4-1.7-.4-2.5 0" fill="#81C784"/>
                        <path d="M8 13h8l1.5 3H6.5l1.5-3z" fill="#FF9800"/>
                        <path d="M8.5 13.5h7l1.2 2.5H7.3l1.2-2.5z" fill="#FFB74D"/>
                        <rect x="7" y="16" width="10" height="5" rx="0.5" fill="#FB8C00"/>
                        <path d="M7.5 16.5h9v4h-9v-4z" fill="#FFA726"/>
                        <ellipse cx="12" cy="21" rx="5.5" ry="0.8" fill="#E65100"/>
                        <rect x="8" y="17" width="2" height="1" rx="0.3" fill="#FFE0B2" opacity="0.4"/>
                      </svg>
                    ), 
                    title: 'Plants & Trees Maintenance', 
                    desc: 'Professional care and maintenance for healthy plants and trees' 
                  },
                  { 
                    icon: (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <rect x="2" y="8" width="14" height="10" fill="#42A5F5" rx="1"/>
                        <rect x="2" y="8" width="14" height="3" fill="#1976D2"/>
                        <path d="M16 10l4-2v10l-4 2V10z" fill="#64B5F6"/>
                        <circle cx="5" cy="20" r="2" fill="#424242"/>
                        <circle cx="5" cy="20" r="1" fill="#BDBDBD"/>
                        <circle cx="13" cy="20" r="2" fill="#424242"/>
                        <circle cx="13" cy="20" r="1" fill="#BDBDBD"/>
                        <circle cx="19" cy="20" r="2" fill="#424242"/>
                        <circle cx="19" cy="20" r="1" fill="#BDBDBD"/>
                        <path d="M6 11c-.5 1-.5 2 0 3 .5 1 1 1.5 0 2.5" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                        <path d="M10 11c-.5 1-.5 2 0 3 .5 1 1 1.5 0 2.5" stroke="#66BB6A" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                      </svg>
                    ), 
                    title: 'Deliver Plants', 
                    desc: 'Quick and reliable plant delivery to your doorstep' 
                  },
                  { 
                    icon: (
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <ellipse cx="12" cy="20" rx="10" ry="2" fill="#A1887F"/>
                        <path d="M8 18c0-1 .5-2 1-3l1-2c.3-.5.5-1 .5-1.5" stroke="#7CB342" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                        <circle cx="6" cy="16" r="2.5" fill="#78909C"/>
                        <circle cx="6" cy="16" r="1.5" fill="#90A4AE"/>
                        <circle cx="12" cy="14" r="3" fill="#607D8B"/>
                        <circle cx="12" cy="14" r="2" fill="#78909C"/>
                        <circle cx="18" cy="16" r="2.5" fill="#78909C"/>
                        <circle cx="18" cy="16" r="1.5" fill="#90A4AE"/>
                        <circle cx="10" cy="10" r="2" fill="#90A4AE"/>
                        <circle cx="10" cy="10" r="1.2" fill="#B0BEC5"/>
                        <circle cx="15" cy="11" r="1.8" fill="#90A4AE"/>
                        <circle cx="15" cy="11" r="1" fill="#B0BEC5"/>
                      </svg>
                    ), 
                    title: 'Stepping Stone', 
                    desc: 'Beautiful stepping stone paths and decorative installations' 
                  }
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    whileHover={{ y: -8, boxShadow: '0 12px 32px rgba(23, 92, 54, 0.15)' }}
                    transition={{ duration: 0.3 }}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      padding: isMobile ? '20px' : '24px',
                      border: '1px solid #e5e5e5',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}>
                    <div style={{
                      fontSize: '2.5rem',
                      marginBottom: '12px',
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      {item.icon}
                    </div>
                    <h3 style={{
                      fontSize: isMobile ? '1.1rem' : '1.2rem',
                      fontWeight: 600,
                      color: '#175c36',
                      marginBottom: '8px',
                      textAlign: 'center'
                    }}>
                      {item.title}
                    </h3>
                    <p style={{
                      fontSize: isMobile ? '0.9rem' : '0.95rem',
                      lineHeight: 1.6,
                      color: '#666',
                      margin: 0,
                      textAlign: 'center'
                    }}>
                      {item.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* Why Choose Us */}
            <motion.section variants={fadeInUp}>
              <div style={{
                background: 'linear-gradient(135deg, #175c36 0%, #1a6b3f 100%)',
                borderRadius: '20px',
                padding: isMobile ? '32px 24px' : '48px 40px',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(23, 92, 54, 0.3)'
              }}>
                {/* Decorative elements */}
                <div style={{
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  pointerEvents: 'none'
                }}/>
                <div style={{
                  position: 'absolute',
                  bottom: '-30px',
                  left: '-30px',
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  pointerEvents: 'none'
                }}/>
                
                <h2 style={{ 
                  fontSize: isMobile ? '1.75rem' : '2.25rem', 
                  fontWeight: 700,
                  marginBottom: '20px',
                  textAlign: 'center',
                  position: 'relative',
                  zIndex: 1
                }}>
                  Why Choose Us?
                </h2>
                
                <p style={{ 
                  fontSize: isMobile ? '1rem' : '1.15rem', 
                  lineHeight: 1.8,
                  marginBottom: '32px',
                  textAlign: 'center',
                  maxWidth: '800px',
                  margin: '0 auto 32px',
                  opacity: 0.95,
                  position: 'relative',
                  zIndex: 1
                }}>
                  At Chiqui's Garden, we pride ourselves on providing excellent customer service, premium quality plants, and expert knowledge. 
                  Whether you're looking to start your first plant collection or expand your existing garden, we're here to help you every step of the way.
                </p>
              </div>
            </motion.section>

            {/* Closing Message */}
            <motion.section 
              variants={fadeInUp}
              style={{
                textAlign: 'center',
                padding: isMobile ? '24px' : '32px 0'
              }}>
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                style={{
                  fontSize: isMobile ? '1.5rem' : '1.75rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #175c36 0%, #2d8659 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '12px'
                }}>
                Let's grow together! 🌱
              </motion.div>
              <p style={{
                fontSize: isMobile ? '0.95rem' : '1.05rem',
                color: '#666',
                fontStyle: 'italic'
              }}>
                Your journey to a greener life starts here
              </p>
            </motion.section>

          </motion.div>
        </motion.div>
      </div>
      
      {/* Footer */}
      <footer style={{
        width: '100%',
        background: 'linear-gradient(135deg, #0f4527 0%, #145a34 50%, #0f4527 100%)',
        marginTop: 'auto',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.03) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '40px 20px 20px',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Main Footer Content */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '40px',
            alignItems: 'start',
            marginBottom: '35px'
          }}>
            {/* Connect Section - Hidden on mobile */}
            {!isMobile && <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Connect With Us</h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <a
                  href="https://www.facebook.com/profile.php?id=61552636677232"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://www.messenger.com/t/61552636677232"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Messenger"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.875 1.44 5.449 3.687 7.126V22l3.486-1.914c.93.257 1.913.395 2.827.395 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm.993 12.437l-2.552-2.725-4.983 2.725 5.48-5.813 2.613 2.725 4.922-2.725-5.48 5.813z"/>
                  </svg>
                </a>
                <a
                  href="viber://chat?number=639368983053"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chat"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.776 6.12 20.36h.003l-.004 2.644s-.037.977.61 1.177c.777.242 1.234-.5 1.977-1.303.41-.44.972-1.084 1.397-1.58 3.85.326 6.812-.417 7.15-.526.776-.253 5.176-.816 5.892-6.657.74-6.02-.36-9.83-2.34-11.546-.596-.55-3.006-2.3-8.375-2.323 0 0-.395-.025-1.026-.017z"/>
                  </svg>
                </a>
              </div>
            </div>}

            {/* Logo Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              justifySelf: 'center',
              marginBottom: isMobile ? '10px' : '0'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2.5px solid rgba(255, 255, 255, 0.25)',
                padding: '10px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                <img
                  src="/images/chiqui_logo_white-outline.svg"
                  alt="Chiqui's Garden Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                  }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  marginBottom: '4px',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>Chiqui's Garden</div>
                <div style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  fontWeight: 400
                }}>© {new Date().getFullYear()} All Rights Reserved</div>
              </div>
            </div>

            {/* Location Section - Hidden on mobile */}
            {!isMobile && <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              justifySelf: 'end'
            }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Our Location</h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                padding: '14px 18px',
                borderRadius: '16px',
                border: '1.5px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.95)">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    marginBottom: '3px',
                    letterSpacing: '0.3px'
                  }}>Iba Garden</div>
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.8rem',
                    lineHeight: '1.3'
                  }}>Pulong Iba, Silang, Cavite</div>
                </div>
              </div>
            </div>}
          </div>

          {/* Divider */}
          <div style={{
            width: '100%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            margin: isMobile ? '20px 0' : '25px 0'
          }} />

          {/* Bottom Links */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? '8px' : '30px',
            paddingBottom: '10px'
          }}>
            <a
              href="/about"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              About Us
            </a>
            <a
              href="/contact"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Contact Info
            </a>
            <a
              href="/return-policy"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Return & Exchange Policy
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Terms of Use
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
      </div>

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <TermsModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        isPrivacyPolicy={true}
      />
    </div>
  );
}

export default AboutUs;
